/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys, Transaction, UserSubscription } from "./types";

const API = axios.create({
    baseURL: `${
        import.meta.env.VITE_APP_WEB_ENDPOINT ||
        "https://admin-develop.habitnu.com"
    }/api`,
    params: {
        uuid: sessionStorage.getItem("uuid"),
        timezone: sessionStorage.getItem("timezone"),
    },
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
    },
});

API.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response.data.error === "User not found") {
            sessionStorage.clear();
            window.location.href = "/";
        }
        return Promise.reject(new Error((error.response?.statusText as string) || 'Request failed'));
    }
);

const updateAxiosInterceptors = () => {
    API.interceptors.request.use(
        (config: import("axios").AxiosRequestConfig) => {
            const params: Record<string, unknown> = {
                ...((config.params as Record<string, unknown>) ?? {}),
                uuid: sessionStorage.getItem("uuid") || "",
                timezone: sessionStorage.getItem("timezone") || "",
            };
            return {
                ...config,
                params,
            };
        }
    );

    return API;
};

export const useSubscription = (uuid: string, timezone: string) =>
    useQuery(
        queryKeys.subscription(),
        async () => {
            const { data } = await updateAxiosInterceptors().get<{
                data: UserSubscription;
            }>("/subscriptions", {
                params: {
                    uuid,
                    timezone,
                },
            });
            return data.data;
        },
        {
            refetchInterval: 5 * 60 * 1000,
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
        }
    );

export const useTransactions = () =>
    useQuery(queryKeys.transaction(), () =>
        updateAxiosInterceptors()
            .get<Transaction[]>("/payments/transactions")
            .then(({ data }) => data)
    );

export const useResetPendingTransaction = () =>
    useMutation(async () => {
        const { data } = await updateAxiosInterceptors().post<{
            success: boolean;
        }>(`/payments/reset_pending_transaction`);
        return data;
    });

export const usePendingTransactions = () =>
    useQuery(
        queryKeys.pendingTransactions(),
        () =>
            updateAxiosInterceptors()
                .get<Transaction[]>("/payments/transactions", {
                    params: {
                        status: "pending",
                    },
                })
                .then(({ data }) => data),
        {
            refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
            retry: 1,
        }
    );

export const useStopAutoRenewal = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (id: string) =>
            updateAxiosInterceptors().post(
                `/subscriptions/${id}/cancel_auto_renew`
            ),
        {
            onSuccess() {
                void queryClient.invalidateQueries("profile");
                void queryClient.invalidateQueries(queryKeys.subscription());
            },
        }
    );
};
